import {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import {
  AppCheckbox,
  AppDatePicker,
  AppFileUploader,
  AppInput,
  AppModal,
  AppNotificationAlert,
  AppPreventNavigation,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import ReactDOMServer from 'react-dom/server';
import axios from 'axios';
import styled from 'styled-components';
import Moment from 'moment';
import { ProgressBar } from 'react-bootstrap';
import { axiosPath } from '../../../../../../routing/helpers/router';
import * as currentPageAction from '../../../../../../store/actions/currentPageAction';
import errorHandler from '@/layout/components/error/ErrorHandler';
import PlantSelect from '../../../../../components/form/Selects/PlantSelect';
import StateSelect from '../../../../../components/form/Selects/StateSelect';
import Translator from '../../../../../components/helpers/Translator';
import colors from '../../../../../../constants/colors';
import minions from '../../../../../../minions/minions';
import useIsMounted from '../../../../../components/hooks/useIsMounted';
import _ from 'lodash';

export default function GGCLMassivePlantEditionTab(props) {
  const { obligationID } = props;
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const { moduleBridgeHex } = useParams();
  const formikRef = useRef();
  const permissions = useSelector(
    (state) => state.module[moduleBridgeHex].permissions
  );
  const obligationLevels = useSelector((s) => s.currentPage?.obligationLevels);
  const plantListTab = useSelector((s) => s.currentPage?.plantListTab);
  const plantList = useSelector(
    (s) => s.currentPage?.plantListTab?.[obligationID]
  );
  const [showBatchProcessModal, setShowBatchProcessModal] = useState(false);
  const [batchProcess, setBatchProcess] = useState();
  const [selectedData, setSelectedData] = useState();
  const [showUnaffectedModal, setShowUnaffectedModal] = useState();
  const [probSevOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="filter" name="no.modify" />
      ),
      value: null,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="filter" name="filter.probability.0" />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="filter" name="filter.probability.1" />
      ),
      value: 1,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="filter" name="filter.probability.2" />
      ),
      value: 2,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator file="filter" name="filter.probability.3" />
      ),
      value: 3,
    },
  ]);
  const [validationSchema] = useState(
    Yup.object().shape({
      plants: Yup.array().min(1, ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
      blocks: Yup.array().min(1, ReactDOMServer.renderToString(<Translator file="module-globallegalCompliance" name="select.a.block.at.least" />)),
      stateBlock: Yup.mixed().test(
        'stateBlock',
        ReactDOMServer.renderToString(<Translator file="user" name="user.required" />),
        function() {
          return (
            !this.parent.blocks.includes('state') ||
            (this.parent.blocks.includes('state') &&
              !_.isEmpty(this.parent.state))
          );
        }
      ),
      calendarBlock: Yup.mixed().test(
        'calendarBlock',
        ReactDOMServer.renderToString(<Translator file="user" name="user.required" />),
        function() {
          return (
            !this.parent.blocks.includes('calendarBlock') ||
            (this.parent.blocks.includes('calendarBlock') &&
              !_.isEmpty(this.parent.calendarDate))
          );
        }
      ),
      evidenceBlock: Yup.mixed().test(
        'evidenceBlock',
        ReactDOMServer.renderToString(<Translator file="user" name="user.required" />),
        function() {
          return (
            !this.parent.blocks.includes('evidence') ||
            (this.parent.blocks.includes('evidence') &&
              !_.isEmpty(this.parent.evidenceName) &&
              !_.isEmpty(this.parent.evidenceDate))
          );
        }
      ),
      evidenceName: Yup.mixed().test(
        'evidenceName',
        ReactDOMServer.renderToString(<Translator file="user" name="user.required" />),
        function() {
          return (
            !this.parent.blocks.includes('evidence') ||
            (this.parent.blocks.includes('evidence') &&
              !_.isEmpty(this.parent.evidenceName))
          );
        }
      ),
      evidenceDate: Yup.mixed().test(
        'evidenceDate',
        ReactDOMServer.renderToString(<Translator file="user" name="user.required" />),
        function() {
          return (
            !this.parent.blocks.includes('evidence') ||
            (this.parent.blocks.includes('evidence') &&
              !_.isEmpty(this.parent.evidenceDate))
          );
        }
      ),
    })
  );

  const plantIDs = useMemo(() => _.map(plantList, 'plantID'), [plantList]);

  const handleMassiveEdition = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_globalLegalCompliance_evaluation_plant_massive_edit"
          ),
          minions.forms.toFormData({
            moduleBridgeHex,
            obligationID,
            tempBatchLoopID: batchProcess?.tempBatchLoopID || null,
            payload: selectedData,
            files: selectedData?.evidenceFiles,
          }),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then((r) => {
          if (isMounted.current) {
            setBatchProcess(r.data);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, obligationID, batchProcess]
  );

  useEffect(() => {
    if (batchProcess && !batchProcess?.isFinished) {
      handleMassiveEdition();
    } else if (batchProcess && batchProcess.isFinished) {
      if (isMounted.current) {
        let updatedObligationIDList = [...plantListTab?.[obligationID]];
        if (
          selectedData?.blocks.includes('state') &&
          batchProcess?.plantObligation
        ) {
          updatedObligationIDList = updatedObligationIDList.map((p) =>
            batchProcess?.plantObligation[p?.plantObligationID]
              ? {
                  ...p,
                  state:
                    batchProcess?.plantObligation[p?.plantObligationID]?.state,
                  customState:
                    batchProcess?.plantObligation[p?.plantObligationID]
                      ?.customState,
                  border:
                    batchProcess?.plantObligation[p?.plantObligationID]?.border,
                }
              : p
          );

          dispatch(
            currentPageAction.change('obligationLevels', {
              ...obligationLevels,
              level3: {
                ...obligationLevels?.level3,
                [obligationID]: batchProcess?.obligation,
              },
            })
          );
        }

        if (selectedData?.blocks.includes('corporate')) {
          updatedObligationIDList = updatedObligationIDList.map((p) =>
            _.has(batchProcess?.isCorporate, p?.plantObligationID)
              ? {
                  ...p,
                  isCorporate: batchProcess?.isCorporate[p?.plantObligationID],
                }
              : p
          );
        }

        dispatch(
          currentPageAction.change('plantListTab', {
            ...plantListTab,
            [obligationID]: updatedObligationIDList,
          })
        );
      }
      if (batchProcess?.unaffected) {
        setShowUnaffectedModal(true);
      } else {
        AppNotificationAlert({
          title: ReactDOMServer.renderToString(
            <Translator file="module-globallegalCompliance" name="completed" />
          ),
          message: ReactDOMServer.renderToString(
            <Translator
              file="module-globallegalCompliance"
              name="success.actions"
            />
          ),
          type: 'success',
          duration: 5000,
        });
      }
      setShowBatchProcessModal(false);
      formikRef.current?.resetForm();
    }
  }, [batchProcess]);
  // < HANDLE MASSIVE EDITION

  const handleBlockSelection = useCallback((block, blocks, setFieldValue) => {
    if (blocks.find((b) => b === block)) {
      setFieldValue(
        'blocks',
        blocks.filter((b) => b !== block)
      );
    } else {
      setFieldValue('blocks', [...blocks, block]);
    }
  }, []);

  return (
    <Container>
      <Formik
        innerRef={formikRef}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={validationSchema}
        initialValues={{
          plants: [],
          blocks: [],
          state: null,
          isCorporate: false,
          probability: null,
          severity: null,
          calendarDate: Moment().format('DD/MM/YYYY'),
          calendarComment: '',
          evidenceName: '',
          evidenceDescription: '',
          evidenceFiles: [],
          evidenceDate: Moment().format('DD/MM/YYYY'),
        }}
        onSubmit={(v) => {
          setSelectedData(v);
          setShowBatchProcessModal(true);
          setBatchProcess([]);
        }}
      >
        {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
          <form onSubmit={handleSubmit}>
            <div className="row mb-2">
              <div className="col-lg-12">
                <PlantSelect
                  type="plants"
                  value={values.plants}
                  onChange={(opt) => setFieldValue('plants', opt)}
                  IDs={plantIDs}
                  error={errors?.plants}
                />
              </div>
            </div>
            <div className="row">
              {(permissions?.ggcl?.states ||
                permissions?.global?.corporateObligations) && (
                <div className="col-12 col-md-6 col-lg-3">
                  {permissions?.ggcl?.states && (
                    <>
                      <AppCheckbox
                        value={values?.blocks.includes('state')}
                        onChange={() =>
                          handleBlockSelection(
                            'state',
                            values.blocks,
                            setFieldValue
                          )
                        }
                        error={errors?.blocks}
                      >
                        <Translator file="user" name="user.state" />
                      </AppCheckbox>
                      <StateSelect
                        onHandleChange={(opt) => setFieldValue('state', opt)}
                        value={values?.state}
                        error={errors?.stateBlock}
                        isMulti={false}
                        moduleBridgeHex={moduleBridgeHex}
                        isClearable
                        isDisabled={!values?.blocks.includes('state')}
                        hideObsolete
                      />
                    </>
                  )}
                  {permissions?.global?.corporateObligations && (
                    <>
                      <AppCheckbox
                        value={values?.blocks.includes('corporate')}
                        onChange={() =>
                          handleBlockSelection(
                            'corporate',
                            values.blocks,
                            setFieldValue
                          )
                        }
                        error={errors?.blocks}
                      >
                        <Translator
                          file="module-globallegalCompliance"
                          name="corporate"
                        />
                      </AppCheckbox>
                      <AppCheckbox
                        value={values?.isCorporate}
                        onChange={(v) => setFieldValue('isCorporate', v)}
                        isDisabled={!values?.blocks.includes('corporate')}
                        hasBorder
                      >
                        <AppRemixIcon icon="building-4" filled />
                        <Translator
                          file="module-legalCompliance"
                          name="corporate.obligation"
                        />
                      </AppCheckbox>
                    </>
                  )}
                </div>
              )}
              {permissions?.ggcl?.probSevRisk && (
                <div className="col-12 col-md-6 col-lg-3">
                  <AppCheckbox
                    value={values?.blocks.includes('probSevRisk')}
                    onChange={() =>
                      handleBlockSelection(
                        'probSevRisk',
                        values.blocks,
                        setFieldValue
                      )
                    }
                    error={errors?.blocks}
                  >
                    <Translator file="module-legalCompliance" name="module.lc.risk" />
                  </AppCheckbox>
                  <AppSingleSelect
                    options={probSevOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator file="filter" name="filter.probability" />
                    )}
                    value={values?.probability}
                    onChange={(opt) => setFieldValue('probability', opt)}
                    isDisabled={!values?.blocks.includes('probSevRisk')}
                  />
                  <AppSingleSelect
                    options={probSevOptions}
                    title={ReactDOMServer.renderToString(
                      <Translator file="filter" name="filter.severity" />
                    )}
                    value={values?.severity}
                    onChange={(opt) => setFieldValue('severity', opt)}
                    isDisabled={!values?.blocks.includes('probSevRisk')}
                  />
                </div>
              )}
              {permissions?.ggcl?.calendar && (
                <div className="col-12 col-md-6 col-lg-3">
                  <AppCheckbox
                    value={values?.blocks.includes('calendar')}
                    onChange={() =>
                      handleBlockSelection(
                        'calendar',
                        values.blocks,
                        setFieldValue
                      )
                    }
                    error={errors?.blocks}
                  >
                    <Translator
                      file="module-globallegalCompliance"
                      name="planned.date"
                    />
                  </AppCheckbox>
                  <AppDatePicker
                    title={ReactDOMServer.renderToString(
                      <Translator file="module-legalCompliance" name="date" />
                    )}
                    value={values.calendarDate}
                    onChange={(opt) => setFieldValue('calendarDate', opt)}
                    popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                    showMonthDropdown
                    showYearDropdown
                    isDisabled={!values?.blocks.includes('calendar')}
                    error={errors.calendarBlock}
                  />
                  <AppTextArea
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="comment"
                      />
                    )}
                    placeholder={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="optional.comment"
                      />
                    )}
                    icon={<AppRemixIcon icon="align-left" noVariants />}
                    onChange={(opt) => setFieldValue('calendarComment', opt)}
                    value={values.calendarComment}
                    isDisabled={!values?.blocks.includes('calendar')}
                  />
                </div>
              )}
              {permissions?.ggcl?.evidences && (
                <div className="col-12 col-md-6 col-lg-3">
                  <AppCheckbox
                    value={values?.blocks.includes('evidence')}
                    onChange={() =>
                      handleBlockSelection(
                        'evidence',
                        values.blocks,
                        setFieldValue
                      )
                    }
                    error={errors?.blocks}
                  >
                    <Translator
                      file="module-legalCompliance"
                      name="module.lc.evidence"
                    />
                  </AppCheckbox>
                  <AppInput
                    title={
                      <Translator file="module-legalCompliance" name="name" />
                    }
                    placeholder={
                      <Translator
                        file="module-legalCompliance"
                        name="name.description.compulsory"
                      />
                    }
                    icon={<AppRemixIcon icon="double-quotes-l" noVariants />}
                    value={values.evidenceName}
                    onChange={(opt) => setFieldValue('evidenceName', opt)}
                    error={errors.evidenceName}
                    isDisabled={!values?.blocks.includes('evidence')}
                    isClearable
                  />
                  <AppTextArea
                    title={
                      <Translator
                        file="module-legalCompliance"
                        name="module.lc.evidence.description"
                      />
                    }
                    placeholder={
                      <Translator
                        file="module-legalCompliance"
                        name="name.description.compulsory"
                      />
                    }
                    icon={<AppRemixIcon icon="align-left" noVariants />}
                    onChange={(opt) =>
                      setFieldValue('evidenceDescription', opt)
                    }
                    value={values.evidenceDescription}
                    isDisabled={!values?.blocks.includes('evidence')}
                  />
                  <AppDatePicker
                    title={
                      <Translator file="module-legalCompliance" name="date" />
                    }
                    value={values.evidenceDate}
                    onChange={(opt) => setFieldValue('evidenceDate', opt)}
                    popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                    isDisabled={!values?.blocks.includes('evidence')}
                  />
                  {values.evidenceFiles.length > 0 && (
                    <EvidenceFileList>
                      {values.evidenceFiles.map((f) => (
                        <li
                          key={f.name}
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="click.delete"
                            />
                          )}
                          onClick={() =>
                            values?.blocks.includes('evidence')
                              ? setFieldValue(
                                  'evidenceFiles',
                                  values.evidenceFiles.filter(
                                    (file) => file.name !== f.name
                                  )
                                )
                              : undefined
                          }
                        >
                          <AppRemixIcon
                            className="pointer mr-3"
                            icon="delete-bin"
                            color={colors.danger}
                          />
                          {minions.files.fileIcon(f.name, true, null, 'mr-2')}
                          {f.name}
                        </li>
                      ))}
                    </EvidenceFileList>
                  )}
                  <AppFileUploader
                    isMulti
                    placeholder={
                      values.evidenceFiles.length === 0
                        ? ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="select.one.multiple.files"
                            />
                          )
                        : ReactDOMServer.renderToString(
                            <Translator
                              file="general"
                              name="select.number.of.elements"
                              variables={{
                                elements: values.evidenceFiles.length,
                              }}
                            />
                          )
                    }
                    onChange={(opt) =>
                      setFieldValue('evidenceFiles', [
                        ...values.evidenceFiles,
                        ...opt,
                      ])
                    }
                    btnText={<Translator file="general" name="search..." />}
                    isDisabled={!values?.blocks.includes('evidence')}
                  />
                </div>
              )}
            </div>
            <div className="row mb-4">
              <div className="col-lg-12">
                <button
                  type="submit"
                  className={`btn btn-primary btn-sm float-right mt-2${
                    isSubmitting ? ' disabled' : ''
                  }`}
                >
                  <Translator
                    file="module-legalCompliance"
                    name="module.lc.blockChange.apply"
                  />
                </button>
              </div>
            </div>
          </form>
        )}
      </Formik>
      <AppModal
        header={`${ReactDOMServer.renderToString(
          <Translator file="module-globallegalCompliance" name="editing" />
        )}...`}
        size="xl"
        show={showBatchProcessModal}
      >
        <AppPreventNavigation />
        <h4>
          {batchProcess?.modalInfo?.message || (
            <Translator file="admin" name="general.precalculating" />
          )}
        </h4>
        <ProgressBar
          animated
          now={batchProcess?.modalInfo?.percent || 5}
          label={`${parseInt(batchProcess?.modalInfo?.percent || 5, 10)}%`}
        />
      </AppModal>
      <AppModal
        title={ReactDOMServer.renderToString(
          <Translator
            file="module-globallegalCompliance"
            name="not.affected.blocks"
          />
        )}
        show={showUnaffectedModal}
        onClose={() => setShowUnaffectedModal(false)}
        onConfirm={() => setShowUnaffectedModal(false)}
        isConfirmBtn
      >
        <ul>
          {batchProcess?.unaffected?.state && (
            <li>
              <b>
                <Translator file="user" name="user.state" />:
              </b>{' '}
              <Translator
                file="module-globallegalCompliance"
                name="unaffected.plants"
                variables={{
                  plants: batchProcess?.unaffected?.state,
                }}
              />
            </li>
          )}
          {batchProcess?.unaffected?.corporate && (
            <li>
              <b>
                <Translator
                  file="module-globallegalCompliance"
                  name="corporate"
                />
                :
              </b>{' '}
              <Translator
                file="module-globallegalCompliance"
                name="unaffected.plants"
                variables={{
                  plants: batchProcess?.unaffected?.corporate,
                }}
              />
            </li>
          )}
          {batchProcess?.unaffected?.calendar && (
            <li>
              <b>
                <Translator file="user" name="user.calendar" />:
              </b>{' '}
              <Translator
                file="module-globallegalCompliance"
                name="unaffected.plants"
                variables={{
                  plants: batchProcess?.unaffected?.calendar,
                }}
              />
            </li>
          )}
          {batchProcess?.unaffected?.evidence && (
            <li>
              <b>
                <Translator
                  file="module-legalCompliance"
                  name="module.lc.evidence"
                />
                :
              </b>{' '}
              <Translator
                file="module-globallegalCompliance"
                name="unaffected.plants"
                variables={{
                  plants: batchProcess?.unaffected?.evidence,
                }}
              />
            </li>
          )}
        </ul>
      </AppModal>
    </Container>
  );
}

const Container = styled.div`
  .col-md-4:not(:last-child) {
    @media (min-width: 768px) {
      border-right: #ddd solid 1px;
    }
  }
`;

const EvidenceFileList = styled.ul`
  list-style: none;
  margin: 10px 0 0 0;
  padding-left: 6px;
  overflow-y: auto;
  max-height: 113px;
  li {
    cursor: pointer;
    display: flex;
    align-items: center;
    border-top: 1px solid ${colors.grey300};
    padding: 8px 0;
    span {
      font-size: 12px;
      color: ${colors.primary};
    }
  }
  li:first-child {
    border-top: 0;
  }
`;
